// =============================================================================
// أنواع TypeScript المشتركة — v3
// لا فترات: التقارير بمدى تاريخي ميلادي، والطلبات يومية مسبقة
// =============================================================================

export type UserRole = "SYSTEM_ADMIN" | "DATA_ENTRY" | "REVIEWER" | "VIEWER";
export type UnitType = "DIRECTORATE" | "BRANCH" | "SECTION";
export type AuditAction =
  | "CREATE" | "UPDATE" | "DELETE" | "APPROVE" | "REOPEN" | "EXPORT"
  | "LOGIN" | "LOGOUT" | "LOGIN_FAILED" | "LOCK" | "LATE_OVERRIDE" | "RECEIVE";

export type ItemUnit =
  | "MEAL" | "PIECE" | "LOAF" | "KG" | "GRAM" | "LITER" | "BOX" | "PACK" | "BUNDLE" | "BOTTLE" | "OTHER";

export interface User {
  id:            number;
  username:      string;
  fullName:      string;
  role:          UserRole;
  directorateId: number | null;
  isActive:      boolean;
  mustChangePwd?: boolean;
  /// حساب محمي — لا يمكن لأي أدمن إعادة تعيين كلمة مروره
  isProtected?:  boolean;
  lastLoginAt:   string | null;
  createdAt:     string;
}

export interface Governorate {
  id:        number;
  nameAr:    string;
  sortOrder: number;
  isActive:  boolean;
}

export interface Directorate {
  id:        number;
  governorateId: number;
  governorate?:  { id: number; nameAr: string };
  nameAr:    string;
  unitType:  UnitType;
  code:      string;
  parentId?: number | null;
  sortOrder: number;
  isActive:  boolean;
}

export interface Restaurant {
  id:            number;
  name:          string;
  governorateId: number;
  isActive:      boolean;
  governorate?:  { id: number; nameAr: string };
}

// ── الأصناف الديناميكية ────────────────────────────────────────────────────

export interface ItemCategory {
  id:        number;
  nameAr:    string;
  nameEn:    string | null;
  sortOrder: number;
  isActive:  boolean;
  items?:    Item[];
}

export interface Item {
  id:            number;
  categoryId:    number;
  code:          string;
  nameAr:        string;
  nameEn:        string | null;
  unit:          ItemUnit;
  /// 0 = عدد صحيح فقط، 1 = منزلة عشرية واحدة على الأكثر
  maxDecimalPlaces: number;
  /// نسبة استحقاق الفرد الواحد من هذا الصنف — اختيارية، للاقتراح فقط
  perPersonRatio: number | null;
  isDefault:     boolean;
  sortOrder:     number;
  isActive:      boolean;
  category?:     { id: number; nameAr: string; sortOrder: number };
}

// ── الطلبات ─────────────────────────────────────────────────────────────────

export interface MealEntryLine {
  id:     number;
  itemId: number;
  qty:    number;
  /// الكمية المستلمة — null حتى يُؤكَّد الاستلام
  receivedQty: number | null;
  item: {
    id: number; code: string; nameAr: string; unit: ItemUnit; maxDecimalPlaces: number;
    category: { id: number; nameAr: string };
  };
}

export interface MealEntry {
  id:             number;
  /// يوم التنفيذ — ميلادي YYYY-MM-DD
  entryDate:      string;
  governorate:    { id: number; nameAr: string };
  directorate:    { id: number; nameAr: string; unitType: UnitType };
  restaurant:     { id: number; name: string } | null;
  lines:          MealEntryLine[];
  notes:          string | null;
  submittedAt:    string;
  lockedAt:       string | null;
  isLateOverride: boolean;
  createdBy:      { id: number; fullName: string };
  createdAt:      string;
  // ── الاستلام ──
  receivedAt:        string | null;
  receivedBy:        { id: number; fullName: string } | null;
  receiptNotes:      string | null;
  receiptReopenedAt: string | null;
  /// محسوبة في الخادم: هل يجوز تسجيل الاستلام الآن؟
  receiptWindow:     { open: boolean; reason?: string; lastDay: string };
}

// ── الأعداد (مؤرَّخة بتاريخ السريان) ─────────────────────────────────────

export interface OrgHeadcount {
  id:             number;
  effectiveFrom:  string;
  governorate:    { id: number; nameAr: string };
  directorate:    { id: number; nameAr: string };
  fullCount:      number;
  allowanceCount: number;
  remaining:      number;
  notes:          string | null;
  createdBy?:     { id: number; fullName: string };
}

export interface HeadcountAsOf {
  id:              number;
  governorateId:   number;
  governorateName: string;
  directorateId:   number;
  directorateName: string;
  fullCount:       number;
  allowanceCount:  number;
  remaining:       number;
  effectiveFrom:   string;
}

// ── التقارير المحورية ──────────────────────────────────────────────────────

export interface PivotColumn {
  key:      string;
  label:    string;
  unit:     ItemUnit;
  category: string;
}
export interface PivotRow {
  id:         number;
  name:       string;
  values:     Record<string, number>;
  total:      number;
  entryCount: number;
}
export type Measure = "requested" | "received" | "diff";
export const MEASURE_LABELS: Record<Measure, string> = {
  requested: "المطلوب", received: "المستلم", diff: "الفرق (المستلم − المطلوب)",
};

export interface PivotReport {
  from:    string;
  to:      string;
  days:    number;
  measure?: Measure;
  title?:  string;
  columns: PivotColumn[];
  rows:    PivotRow[];
  totals:  Record<string, number>;
}

// ── التدقيق والاستجابات ────────────────────────────────────────────────────

export interface AuditLog {
  id:         number;
  at:         string;
  user:       { id: number; fullName: string; username: string } | null;
  action:     AuditAction;
  entity:     string;
  entityId:   number | null;
  summary:    string | null;
  beforeJson: unknown;
  afterJson:  unknown;
  ipAddress:  string | null;
}

export interface ApiMeta {
  total:      number;
  page:       number;
  pageSize:   number;
  totalPages: number;
}

export interface ApiList<T> { data: T[]; meta: ApiMeta }
export interface ApiItem<T> { data: T; message?: string }

// ── تسميات ─────────────────────────────────────────────────────────────────

export const ITEM_UNIT_LABELS: Record<ItemUnit, string> = {
  MEAL: "وجبة", PIECE: "قطعة", LOAF: "رغيف", KG: "كغم", GRAM: "غرام",
  LITER: "ليتر", BOX: "صندوق", PACK: "عبوة", BUNDLE: "ربطة", BOTTLE: "قارورة", OTHER: "أخرى",
};

export const ROLE_LABELS: Record<UserRole, string> = {
  SYSTEM_ADMIN: "مدير النظام",
  DATA_ENTRY:   "مدخل بيانات",
  REVIEWER:     "مراجع",
  VIEWER:       "مشاهد",
};

export const UNIT_TYPE_LABELS: Record<UnitType, string> = {
  DIRECTORATE: "مديرية",
  BRANCH:      "فرع",
  SECTION:     "قسم",
};

export const AUDIT_ACTION_LABELS: Record<AuditAction, string> = {
  CREATE: "إنشاء", UPDATE: "تعديل", DELETE: "حذف", APPROVE: "اعتماد",
  REOPEN: "إعادة فتح", EXPORT: "تصدير", LOGIN: "دخول", LOGOUT: "خروج",
  LOGIN_FAILED: "دخول فاشل", LOCK: "إقفال", LATE_OVERRIDE: "تجاوز الموعد",
  RECEIVE: "تأكيد استلام",
};

// ── معايير الاستحقاق (منفصلة عن أعداد الكيانات العامة) ─────────────────────

export interface ConsumptionNormAsOf {
  id:             number;
  directorateId:  number;
  directorateName: string;
  itemId:         number;
  itemCode:       string;
  itemName:       string;
  targetQty:      number;
  effectiveFrom:  string;
}

export interface CoverageRow {
  directorateId:   number;
  directorateName: string;
  governorateName: string;
  itemId:          number;
  itemCode:        string;
  itemName:        string;
  unit:            ItemUnit;
  totalQty:        number;
  avgDaily:        number;
  target:          number | null;
  ratio:           number | null;
}
export interface CoverageReport { from: string; to: string; days: number; rows: CoverageRow[] }
